import { num } from '../format';

/**
 * Checking a checkpoint (state/useCheckpoint.ts, lib/checkpoint.ts).
 *
 * "Check my work" on a checkpoint does what it does on every level, from the
 * learner's side: they press it, the objectives resolve, the level clears. It
 * differs only in *where* the checks run, and none of these sentences says
 * where. That is a decision, not an omission. A learner does not need to know
 * about a grading service to use one, and every sentence that named it —
 * "server verification", "confirmed by the server", "submit this workspace" —
 * turned one button into two and left the learner to work out which was the
 * real one — while the familiar one, "Check my work", failed outright.
 *
 * What they *do* need is what the checks cannot see and the allowance that
 * limits them, because both change what to do next: work that is not
 * committed gets progress checks, and a check on unchanged work is not spent
 * at all. Every refusal says what to do about it.
 */
export const en = {
  /** Under the objectives while a check is running, which is tens of seconds rather than one. */
  checking: 'Assessing the work you submitted. You can keep editing and checking progress; these checks will not submit another assessment.',
  /** A long-running assessment continues in the background. */
  stillChecking: 'The assessment is taking longer than usual. Its result will appear automatically. Free progress checks are still available.',
  reconnecting: 'The assessment connection was interrupted. We will reconnect to the same attempt. You can still check progress.',
  retryConnection: 'Retry connection',
  waitingAllowance: 'This snapshot will be assessed automatically when another check is available. Check again to replace it with your latest work.',
  earlierPassed: 'Earlier submitted work passed',
  earlierFailed: 'Earlier submitted work needs changes',
  earlierDetail: 'This assessment belongs to the work submitted before your latest progress check. The objectives above describe your more recent check.',
  progressChecked: 'Progress checked',
  progressDetail: 'These are checks of your branch, working tree and own tests. Other objectives are assessed when the work is ready.',
  checkingProgress: 'Checking your progress first. If your committed work is ready, this check also submits it for assessment; unchanged submissions are free.',
  progressOnly: 'Keep working and check again as often as you need. No assessment check was used.',
  ready: 'The readiness checks passed. Assessing the remaining requirements.',
  earlierWork: 'This result describes earlier work. Check again to include your latest changes.',
  badgeEarned: 'Badge already earned. Checks here describe your current work.',
  testOutput: 'Your test output',
  branchFailed: 'The current branch does not match the branch required in the brief.',
  suiteFailed: 'Your tests failed or no tests ran. Review the test output below.',
  dirty: 'There are uncommitted changes. You can keep checking progress; commit your work when it is ready for assessment.',
  signIn: 'Sign in from the account menu to assess your work for a badge. Progress checks remain free.',
  unavailable: 'Your work could not be checked just now. Try again in a few minutes.',
  submission: 'This workspace could not be sent for checking. Look for very large files or symbolic links in storefront/, then try again.',
  busyElsewhere: 'Another checkpoint of yours is still being checked. Try again once it finishes.',
  paused: 'Checkpoint checks are paused for everyone at the moment. Try again later.',
  quota: 'You have used the checks available for now.',
  waitFor: (minutes: number) => minutes < 1
    ? 'You have used the checks available for now. Another is available in less than a minute.'
    : `You have used the checks available for now. Another is available in ${num(minutes, 'en')} ${minutes === 1 ? 'minute' : 'minutes'}.`,
  allowance: (used: number, limit: number) =>
    `${num(used, 'en')} of ${num(limit, 'en')} assessment checks used this hour. Progress checks and unchanged submissions are free.`,
  /**
   * The one row every hidden failure shares. Drawn after "Hidden check:", like
   * a level's; unlike a level's it never names the check, because a
   * checkpoint ships no titles for them (lib/checkpoint.ts says why).
   */
  hiddenFailed: 'additional requirements are not yet satisfied; review the brief and constraints',
  /**
   * The grader's gates. Each refuses the whole submission before a single
   * objective runs, so each has to say what to change — "not yet" alone would
   * leave a learner re-checking the same tree.
   */
  gates: {
    'repository-integrity': 'The repository is damaged: git fsck reports errors in it. Reset the level to start again from a clean copy.',
    clean: 'Commit everything first. Only committed work is checked, and storefront/ has changes that are not committed: edited, staged or new files. Files your .gitignore covers are left out.',
    baseline: 'main is not where this checkpoint put it. Leave main as it was and do the work on a branch.',
  },
};

const minutesAr = (m: number) => m === 1 ? 'دقيقة واحدة' : m === 2 ? 'دقيقتين'
  : m <= 10 ? `${num(m, 'ar')} دقائق` : `${num(m, 'ar')} دقيقة`;

export const ar: typeof en = {
  checking: 'جارٍ تقييم العمل الذي أرسلته. يمكنك متابعة التعديل وفحص تقدّمك؛ لن ترسل هذه الفحوصات تقييمًا آخر.',
  stillChecking: 'يستغرق التقييم وقتًا أطول من المعتاد. ستظهر النتيجة تلقائيًا. ما زالت فحوصات التقدّم المجانية متاحة.',
  reconnecting: 'انقطع الاتصال بالتقييم. سنعيد الاتصال بالمحاولة نفسها. ما زال بإمكانك فحص تقدّمك.',
  retryConnection: 'أعِد الاتصال',
  waitingAllowance: 'سيُقيَّم هذا العمل تلقائيًا حين يتوفّر فحص آخر. افحص مجددًا لاستبداله بعملك الأحدث.',
  earlierPassed: 'اجتاز العمل المُرسل سابقًا التقييم',
  earlierFailed: 'يحتاج العمل المُرسل سابقًا إلى تغييرات',
  earlierDetail: 'يخصّ هذا التقييم العمل الذي أرسلته قبل أحدث فحص لتقدّمك. تصف الأهداف أعلاه الفحص الأحدث.',
  progressChecked: 'تم فحص التقدّم',
  progressDetail: 'هذه فحوصات للفرع وشجرة العمل واختباراتك. تُقيَّم بقية الأهداف عندما يصبح العمل جاهزًا.',
  checkingProgress: 'نفحص تقدّمك أولًا. إذا كان عملك المُودَع جاهزًا، يُرسل هذا الفحص أيضًا للتقييم؛ إعادة إرسال العمل نفسه مجانية.',
  progressOnly: 'تابع العمل وتحقّق منه كلما احتجت. لم يُستخدم أي فحص تقييم.',
  ready: 'اجتاز العمل فحوصات الجاهزية. جارٍ تقييم بقية المتطلبات.',
  earlierWork: 'هذه النتيجة تخصّ نسخة سابقة من عملك. تحقّق مجددًا لتشمل أحدث تغييراتك.',
  badgeEarned: 'حصلت على الشارة بالفعل. الفحوصات هنا تخصّ عملك الحالي.',
  testOutput: 'مخرجات اختباراتك',
  branchFailed: 'الفرع الحالي لا يطابق الفرع المطلوب في التكليف.',
  suiteFailed: 'فشلت اختباراتك أو لم يُشغَّل أي اختبار. راجع المخرجات أدناه.',
  dirty: 'توجد تغييرات لم تدخل في commit. يمكنك متابعة فحص تقدّمك؛ احفظ العمل في commit عندما يصبح جاهزًا للتقييم.',
  signIn: 'سجّل الدخول من قائمة الحساب لتقييم عملك والحصول على الشارة. تبقى فحوصات التقدّم مجانية.',
  unavailable: 'تعذّر فحص عملك الآن. حاول مجددًا بعد بضع دقائق.',
  submission: 'تعذّر إرسال مساحة العمل للفحص. ابحث عن ملفات كبيرة جدًا أو روابط رمزية داخل storefront/ ثم حاول مجددًا.',
  busyElsewhere: 'ما زال فحص محطة أخرى لك جاريًا. حاول مجددًا بعد أن ينتهي.',
  paused: 'فحص المحطات متوقّف مؤقتًا للجميع. حاول لاحقًا.',
  quota: 'استخدمت الفحوصات المتاحة الآن.',
  waitFor: (minutes: number) => minutes < 1
    ? 'استخدمت الفحوصات المتاحة الآن. يتوفّر فحص آخر خلال أقل من دقيقة.'
    : `استخدمت الفحوصات المتاحة الآن. يتوفّر فحص آخر بعد ${minutesAr(minutes)}.`,
  allowance: (used: number, limit: number) =>
    `استُخدم ${num(used, 'ar')} من ${num(limit, 'ar')} فحوصات التقييم هذه الساعة. فحوصات التقدّم وإعادة إرسال العمل نفسه مجانية.`,
  hiddenFailed: 'لم تتحقّق بعض المتطلبات الإضافية بعد؛ راجع التكليف والقيود',
  /* `commit`, `main`, `git fsck` and the folder names stay in Latin script:
     they are what the learner types (docs/i18n.md). */
  gates: {
    'repository-integrity': 'المستودع تالف: يُبلغ git fsck عن أخطاء فيه. أعد ضبط المستوى لتبدأ من نسخة سليمة.',
    clean: 'احفظ كل شيء في commit أولًا. لا يُفحص إلا ما دخل في commit، وفي storefront/ تغييرات لم تدخل بعد: ملفات معدّلة أو مُجهّزة أو جديدة. الملفات التي يغطّيها .gitignore لا تُحتسب.',
    baseline: 'الفرع main ليس حيث وضعته هذه المحطة. اترك main كما كان وأنجز العمل على فرع.',
  },
};

export const de: typeof en = {
  checking: 'Die eingereichte Arbeit wird bewertet. Du kannst weiter bearbeiten und deinen Fortschritt prüfen; diese Prüfungen reichen keine weitere Bewertung ein.',
  stillChecking: 'Die Bewertung dauert länger als üblich. Ihr Ergebnis erscheint automatisch. Kostenlose Fortschrittsprüfungen stehen weiter zur Verfügung.',
  reconnecting: 'Die Verbindung zur Bewertung wurde unterbrochen. Wir verbinden uns wieder mit demselben Versuch. Du kannst weiter deinen Fortschritt prüfen.',
  retryConnection: 'Erneut verbinden',
  waitingAllowance: 'Diese Momentaufnahme wird automatisch bewertet, sobald wieder eine Prüfung verfügbar ist. Prüfe erneut, um sie durch deinen neuesten Stand zu ersetzen.',
  earlierPassed: 'Früher eingereichte Arbeit hat bestanden',
  earlierFailed: 'Früher eingereichte Arbeit braucht Änderungen',
  earlierDetail: 'Diese Bewertung gehört zu der Arbeit, die vor deiner letzten Fortschrittsprüfung eingereicht wurde. Die Ziele oben beschreiben deine neuere Prüfung.',
  progressChecked: 'Fortschritt geprüft',
  progressDetail: 'Das sind Prüfungen deines Branches, deines Arbeitsverzeichnisses und deiner eigenen Tests. Die übrigen Ziele werden bewertet, wenn die Arbeit fertig ist.',
  checkingProgress: 'Zuerst wird dein Fortschritt geprüft. Ist deine committete Arbeit bereit, wird sie mit dieser Prüfung auch zur Bewertung eingereicht; unveränderte Einreichungen sind kostenlos.',
  progressOnly: 'Arbeite weiter und prüfe so oft, wie du willst. Es wurde keine Bewertung verbraucht.',
  ready: 'Die Vorprüfungen sind bestanden. Die übrigen Anforderungen werden bewertet.',
  earlierWork: 'Dieses Ergebnis beschreibt einen früheren Stand. Prüfe noch einmal, um deine letzten Änderungen einzubeziehen.',
  badgeEarned: 'Abzeichen bereits verdient. Prüfungen hier beschreiben deinen aktuellen Stand.',
  testOutput: 'Ausgabe deiner Tests',
  branchFailed: 'Der aktuelle Branch ist nicht der, den der Auftrag verlangt.',
  suiteFailed: 'Deine Tests sind fehlgeschlagen, oder es liefen keine. Sieh dir unten die Testausgabe an.',
  dirty: 'Es gibt Änderungen, die nicht committet sind. Du kannst weiter deinen Fortschritt prüfen; committe deine Arbeit, wenn sie bereit für die Bewertung ist.',
  signIn: 'Melde dich über das Kontomenü an, um deine Arbeit für ein Abzeichen bewerten zu lassen. Fortschrittsprüfungen bleiben kostenlos.',
  unavailable: 'Deine Arbeit konnte gerade nicht geprüft werden. Versuch es in ein paar Minuten noch einmal.',
  submission: 'Dieser Arbeitsstand konnte nicht zur Prüfung geschickt werden. Such in storefront/ nach sehr großen Dateien oder symbolischen Links und versuch es dann noch einmal.',
  busyElsewhere: 'Ein anderer Checkpoint von dir wird noch geprüft. Versuch es noch einmal, wenn er fertig ist.',
  paused: 'Checkpoint-Prüfungen sind im Moment für alle pausiert. Versuch es später noch einmal.',
  quota: 'Du hast die Prüfungen verbraucht, die gerade verfügbar sind.',
  waitFor: (minutes) =>
    minutes < 1
      ? 'Du hast die Prüfungen verbraucht, die gerade verfügbar sind. In weniger als einer Minute gibt es eine neue.'
      : `Du hast die Prüfungen verbraucht, die gerade verfügbar sind. In ${num(minutes, 'de')} ${minutes === 1 ? 'Minute' : 'Minuten'} gibt es eine neue.`,
  allowance: (used, limit) =>
    `${num(used, 'de')} von ${num(limit, 'de')} Bewertungen in dieser Stunde verbraucht. Fortschrittsprüfungen und unveränderte Einreichungen sind kostenlos.`,
  hiddenFailed: 'weitere Anforderungen sind noch nicht erfüllt; lies den Auftrag und die Vorgaben noch einmal',
  gates: {
    'repository-integrity': 'Das Repository ist beschädigt: git fsck meldet Fehler darin. Setz das Level zurück, um mit einer sauberen Kopie neu anzufangen.',
    clean: 'Committe zuerst alles. Geprüft wird nur committete Arbeit, und storefront/ hat Änderungen, die nicht committet sind: bearbeitete, gestagte oder neue Dateien. Dateien, die deine .gitignore abdeckt, bleiben außen vor.',
    baseline: 'main steht nicht dort, wo dieser Checkpoint es hingesetzt hat. Lass main, wie es war, und arbeite auf einem Branch.',
  },
};

export const ja: typeof en = {
  checking: '提出された作業を評価しています。編集や進捗チェックは続けられます。これらのチェックで新たに評価が提出されることはありません。',
  stillChecking: '評価に通常より時間がかかっています。結果は自動的に表示されます。無料の進捗チェックは引き続き使えます。',
  reconnecting: '評価との接続が切れました。同じ試行に再接続します。進捗チェックは引き続き使えます。',
  retryConnection: '再接続',
  waitingAllowance: '次のチェックが利用可能になると、この作業内容を自動で評価します。最新の作業に置き換えるには、もう一度チェックしてください。',
  earlierPassed: '以前に提出した作業は合格しました',
  earlierFailed: '以前に提出した作業には修正が必要です',
  earlierDetail: 'この評価は、直近の進捗チェックより前に提出された作業のものです。上の目標には、より新しいチェックの結果が表示されています。',
  progressChecked: '進捗をチェックしました',
  progressDetail: 'ブランチ、作業ツリー、あなた自身のテストをチェックした結果です。そのほかの目標は、作業が整った時点で評価されます。',
  checkingProgress: 'まず進捗をチェックします。コミット済みの作業が整っていれば、このチェックから評価にも提出されます。変更のない再提出は無料です。',
  progressOnly: '作業を続けて、必要なだけ何度でもチェックしてください。評価チェックは消費していません。',
  ready: '準備チェックに合格しました。残りの要件を評価しています。',
  earlierWork: 'この結果は以前の作業についてのものです。最新の変更を反映するには、もう一度チェックしてください。',
  badgeEarned: 'バッジは獲得済みです。ここでのチェックは現在の作業の状態を示します。',
  testOutput: 'テストの出力',
  branchFailed: '現在のブランチが、ブリーフで指定されたブランチと一致していません。',
  suiteFailed: 'テストが失敗したか、テストが1つも実行されませんでした。下のテスト出力を確認してください。',
  dirty: 'コミットされていない変更があります。進捗チェックは続けられます。評価を受ける準備ができたら、作業をコミットしてください。',
  signIn: 'バッジのための評価を受けるには、アカウントメニューからログインしてください。進捗チェックは引き続き無料です。',
  unavailable: '今は作業をチェックできませんでした。数分後にもう一度お試しください。',
  submission: 'このワークスペースをチェックのために送信できませんでした。storefront/ に非常に大きなファイルやシンボリックリンクがないか確認して、もう一度お試しください。',
  busyElsewhere: '別のチェックポイントをチェック中です。終わってからもう一度お試しください。',
  paused: '現在、チェックポイントのチェックはすべてのユーザーで一時停止しています。しばらくしてからもう一度お試しください。',
  quota: '現在使えるチェックをすべて使い切りました。',
  waitFor: (minutes: number) =>
    minutes < 1
      ? '現在使えるチェックをすべて使い切りました。1分以内に次のチェックが使えるようになります。'
      : `現在使えるチェックをすべて使い切りました。次のチェックは${num(minutes, 'ja')}分後に使えます。`,
  allowance: (used: number, limit: number) =>
    `この1時間で評価チェックを${num(limit, 'ja')}回中${num(used, 'ja')}回使いました。進捗チェックと、変更のない提出は無料です。`,
  hiddenFailed: 'まだ満たされていない要件があります。ブリーフと制約を確認してください',
  gates: {
    'repository-integrity': 'リポジトリが壊れています。git fsck がエラーを報告しています。レベルをリセットして、クリーンなコピーからやり直してください。',
    clean: '先にすべてコミットしてください。チェックされるのはコミット済みの作業だけですが、storefront/ にはコミットされていない変更(編集済み、ステージ済み、または新規のファイル)があります。.gitignore の対象になっているファイルは含まれません。',
    baseline: 'main が、このチェックポイントが置いた位置にありません。main は元のままにして、ブランチで作業してください。',
  },
};
